// Copyright 2021 The Casdoor Authors. All Rights Reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//      http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import {Button, Input, Space} from "antd";
import React, {useEffect, useRef, useState} from "react";
import i18next from "i18next";
import {SafetyOutlined} from "@ant-design/icons";
import {RsoCaptchaSmartLogin} from "./RsoCaptchaSmartLogin";
import {sendCode} from "../backend/UserBackend";
import * as Setting from "../Setting";

export const CURRENT_EXECUTING_STEP = {
  FAST_LOGIN: "fast_login",
};

export const SEND_CODE_METHOD = {
  SMART_LOGIN: "smartLogin",
  FAST_LOGIN: "fast_login",
};

export const RsoSendCodeInput = ({shouldReloadCaptcha, codeValue, isOtpSent = false, disabled, textBefore, onChange, onButtonClickArgs, application, method, countryCode, onSend}) => {
  const [buttonLeftTime, setButtonLeftTime] = useState(0);
  const [isCaptchaCorrect, setIsCaptchaCorrect] = useState(false);
  const [isCaptchaFalse, setIsCaptchaFalse] = useState(0);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaImg, setCaptchaImg] = useState("");
  const [clientSecret, setClientSecret] = useState("");

  const timerRef = useRef(null);
  const getCodeResendTimeout = () => {
    return (application && application.codeResendTimeout > 0) ? application.codeResendTimeout : 60;
  };

  const handleCountDown = (leftTime) => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    setButtonLeftTime(leftTime);

    timerRef.current = setInterval(() => {
      setButtonLeftTime(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // eslint-disable-next-line unused-imports/no-unused-vars
  const handleOk = async() => {
    if (isCaptchaCorrect) {
      setCaptchaImg("");
      setCaptchaToken("");
      setClientSecret("");
      setIsCaptchaCorrect(false);
      return;
    }
    setButtonLoading(true);
    try {
      const response = await sendCode("Default", captchaToken, clientSecret, method, countryCode, ...onButtonClickArgs);
      if (response.status === "ok") {
        Setting.showMessage("success", i18next.t("user:Verification code sent"));
        handleCountDown(getCodeResendTimeout());
        setIsCaptchaCorrect(true);
        onSend();
      } else {
        setCaptchaImg("");
        setCaptchaToken("");
        setClientSecret("");
        setIsCaptchaFalse((prev) => prev + 1);
        Setting.showMessage("error", response.msg);
      }

    } catch (err) {
      setCaptchaImg("");
      setCaptchaToken("");
      setClientSecret("");
      setIsCaptchaFalse((prev) => prev + 1);
    } finally {
      setButtonLoading(false);
    }
  };
  const prevReloadCount = useRef(shouldReloadCaptcha);
  useEffect(() => {
    if (shouldReloadCaptcha !== prevReloadCount.current) {
      prevReloadCount.current = shouldReloadCaptcha;
      setCaptchaImg("");
      setCaptchaToken("");
      setClientSecret("");
      setIsCaptchaCorrect(false);
    }
  }, [shouldReloadCaptcha]);
  // eslint-disable-next-line unused-imports/no-unused-vars
  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  return (
    <React.Fragment>
      {!isCaptchaCorrect && (
        <RsoCaptchaSmartLogin
          isCaptchaFalse={isCaptchaFalse}
          owner={application.owner}
          name={application.name}
          setCaptchaToken={(value) => setCaptchaToken(value)}
          setCaptchaImg={(value) => setCaptchaImg(value)}
          setClientSecret={(value) => setClientSecret(value)}
          captchaImg={captchaImg}
          captchaToken={captchaToken}
          isCurrentProvider={false}
        />
      )}
      <Space.Compact>
        {textBefore && (
          <Space.Addon>
            {textBefore}
          </Space.Addon>
        )}
        <Input
          className="with-addon-en-input"
          disabled={!isOtpSent}
          value={codeValue}
          prefix={<SafetyOutlined />}
          placeholder={i18next.t("code:Enter your code")}
          onChange={e => onChange(e.target.value)}
          autoComplete="one-time-code"
        />
        <Button onClick={handleOk} style={{fontSize: 14, height: "auto"}} type={"primary"} disabled={disabled || buttonLeftTime > 0} loading={buttonLoading}>
          {buttonLeftTime > 0 ? `${buttonLeftTime} s` : buttonLoading ? i18next.t("code:Sending") : i18next.t("code:Send Code")}
        </Button>
      </Space.Compact>
    </React.Fragment>
  );
};
